import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import {
  dealManufacturerPayablesOverageHints,
  manufacturerNameSuggestions,
  manufacturerPayableRemainingUsd,
  payablesCollectedUsdFromDealMetadata,
  type DealPayableOverageHint,
  type ManufacturerPayableCategory,
} from "@/lib/ops/manufacturerPayablesMath";
import { inferSalePathKind, parseAllowedDepositUsd } from "@/lib/opsDealTypes/saleTypeProviders";
import { listDealSaleTypes } from "@/lib/opsDealTypesClient";
import type { DealRow } from "./opsPaperworkTypes";
import { OpsManufacturerPayableCreateForm } from "./OpsManufacturerPayableCreateForm";
import { OpsManufacturerPayableOverageHints } from "./OpsManufacturerPayableOverageHints";
import { OpsManufacturerPayableRow } from "./OpsManufacturerPayableRow";
import {
  createOpsManufacturerPayable,
  listOpsManufacturerPayables,
  patchOpsManufacturerPayable,
  type ManufacturerPayable,
  type ManufacturerPayableTotals,
} from "@/lib/opsManufacturerPayablesClient";

function money(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

export function OpsManufacturerPayablesTab(props: {
  deals: DealRow[];
  presetDealId?: string | null;
}) {
  const [status, setStatus] = useState<"open_due" | "all" | "paid">("open_due");
  const [rows, setRows] = useState<ManufacturerPayable[]>([]);
  const [totals, setTotals] = useState<ManufacturerPayableTotals | null>(null);
  const [linkedDealIds, setLinkedDealIds] = useState<Set<string>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [category, setCategory] = useState<ManufacturerPayableCategory>("steel");
  const [collected, setCollected] = useState("");
  const [allowed, setAllowed] = useState("");
  const [dealId, setDealId] = useState(props.presetDealId ?? "");
  const [notes, setNotes] = useState("");
  const [saleTypeDeposits, setSaleTypeDeposits] = useState<number[]>([]);
  const [pathDeposits, setPathDeposits] = useState<
    Array<{ path: "cash" | "rto" | "finance"; amount: number }>
  >([]);

  useEffect(() => {
    if (props.presetDealId) setDealId(props.presetDealId);
  }, [props.presetDealId]);

  const prefillDealIdRef = useRef("");
  useEffect(() => {
    if (!dealId) {
      prefillDealIdRef.current = "";
      return;
    }
    const deal = props.deals.find((d) => d.id === dealId);
    const usd = payablesCollectedUsdFromDealMetadata(deal?.metadata);
    if (usd == null) return;
    if (prefillDealIdRef.current === dealId) return;
    prefillDealIdRef.current = dealId;
    setCollected(String(usd));
  }, [dealId, props.deals]);

  const reload = useCallback(async () => {
    setBusy(true);
    const filter = status === "open_due" ? undefined : status === "paid" ? "paid" : "all";
    const [r, all] = await Promise.all([
      listOpsManufacturerPayables({ status: filter }),
      listOpsManufacturerPayables({ status: "all" }),
    ]);
    setBusy(false);
    if (all.ok) {
      setLinkedDealIds(
        new Set(all.data.payables.map((p) => p.deal_id).filter((id): id is string => Boolean(id)))
      );
    }
    if (!r.ok) {
      setMsg(r.error);
      setRows([]);
      setTotals(null);
      return;
    }
    setMsg("");
    setRows(r.data.payables);
    setTotals(r.data.totals);
  }, [status]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    void (async () => {
      const r = await listDealSaleTypes();
      if (!r.ok) return;
      const pathRows: Array<{ path: "cash" | "rto" | "finance"; amount: number }> = [];
      const amounts: number[] = [];
      for (const t of r.data.sale_types) {
        const n = parseAllowedDepositUsd(t.metadata);
        if (n == null || n <= 0) continue;
        amounts.push(n);
        const kind = inferSalePathKind(t.type_key, t.display_name);
        if (kind === "cash" || kind === "rto" || kind === "finance") {
          pathRows.push({ path: kind, amount: n });
        }
      }
      setPathDeposits(pathRows);
      setSaleTypeDeposits([...new Set(amounts)].sort((a, b) => a - b));
    })();
  }, []);

  const allowedPrefillRef = useRef(false);
  useEffect(() => {
    if (allowedPrefillRef.current || allowed) return;
    if (saleTypeDeposits.length === 1) {
      allowedPrefillRef.current = true;
      setAllowed(String(saleTypeDeposits[0]));
    }
  }, [saleTypeDeposits, allowed]);

  const manufacturerSuggestions = useMemo(
    () =>
      manufacturerNameSuggestions([
        ...rows.map((r) => r.manufacturer),
        ...(totals?.by_manufacturer.map((m) => m.manufacturer) ?? []),
      ]),
    [rows, totals]
  );

  const overageHints = useMemo(
    () =>
      dealManufacturerPayablesOverageHints({
        deals: props.deals,
        linkedDealIds,
        uniqueOrgDeposits: saleTypeDeposits,
        depositsForSalePath: pathDeposits,
      }),
    [props.deals, linkedDealIds, saleTypeDeposits, pathDeposits]
  );

  function fillFromOverage(hint: DealPayableOverageHint) {
    prefillDealIdRef.current = hint.deal_id;
    setDealId(hint.deal_id);
    setCollected(String(hint.collected_usd));
    setAllowed(String(hint.allowed_deposit_usd));
  }

  async function onCreate() {
    setBusy(true);
    const r = await createOpsManufacturerPayable({
      manufacturer,
      category,
      collected_usd: Number(collected) || 0,
      allowed_deposit_usd: Number(allowed) || 0,
      deal_id: dealId || null,
      notes: notes.trim() || undefined,
    });
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setCollected("");
    setAllowed("");
    setNotes("");
    await reload();
  }

  async function markPaid(row: ManufacturerPayable) {
    const remaining = manufacturerPayableRemainingUsd(row.amount_owed_usd, row.amount_paid_usd);
    setBusy(true);
    const r = await patchOpsManufacturerPayable({
      id: row.id,
      status: "paid",
      amount_paid_usd: row.amount_owed_usd,
      paid_reference: remaining > 0 ? "marked paid" : row.paid_reference,
    });
    setBusy(false);
    if (!r.ok) setMsg(r.error);
    else await reload();
  }

  async function markDue(row: ManufacturerPayable) {
    setBusy(true);
    const r = await patchOpsManufacturerPayable({ id: row.id, status: "due" });
    setBusy(false);
    if (!r.ok) setMsg(r.error);
    else await reload();
  }

  async function recordPay(row: ManufacturerPayable, paymentUsd: number) {
    setBusy(true);
    const r = await patchOpsManufacturerPayable({
      id: row.id,
      payment_usd: paymentUsd,
    });
    setBusy(false);
    if (!r.ok) setMsg(r.error);
    else await reload();
  }

  return (
    <div className="space-y-4">
      {totals ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/40">
          <div className="text-xs uppercase tracking-wide text-amber-900 dark:text-amber-100">
            Manufacturer payback still to go out
          </div>
          <div className="text-2xl font-semibold text-amber-950 dark:text-amber-50">
            {money(totals.remaining_usd)}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-amber-900 dark:text-amber-100">
            {totals.by_manufacturer.map((m) => (
              <span key={m.manufacturer} className="rounded-full border border-amber-300 px-2 py-0.5">
                {m.manufacturer}: {money(m.remaining_usd)} ({m.count})
              </span>
            ))}
            {totals.by_category.map((c) => (
              <span key={c.category} className="rounded-full bg-white/60 px-2 py-0.5 dark:bg-zinc-900/40">
                {c.category}: {money(c.remaining_usd)}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {msg ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">{msg}</p>
      ) : null}

      <InlineHelp label="How manufacturer payables work">
        When a customer pays more than the dealer deposit (or the full balance up front), the overage is
        manufacturer AP. Example: collect $12,000, keep $1,500, owe $10,500 — record payments until the
        remaining total hits zero.
      </InlineHelp>

      <OpsManufacturerPayableOverageHints hints={overageHints} onFill={fillFromOverage} />

      <OpsManufacturerPayableCreateForm
        deals={props.deals}
        manufacturer={manufacturer}
        setManufacturer={setManufacturer}
        manufacturerSuggestions={manufacturerSuggestions}
        category={category}
        setCategory={setCategory}
        collected={collected}
        setCollected={setCollected}
        allowed={allowed}
        setAllowed={setAllowed}
        allowedDepositSuggestions={saleTypeDeposits}
        dealId={dealId}
        setDealId={setDealId}
        notes={notes}
        setNotes={setNotes}
        busy={busy}
        onCreate={() => void onCreate()}
      />

      <div className="flex gap-2">
        {(["open_due", "paid", "all"] as const).map((s) => (
          <button
            key={s}
            type="button"
            className={`rounded-full px-3 py-1 text-sm ${status === s ? "bg-indigo-600 text-white" : "bg-gray-100"}`}
            onClick={() => setStatus(s)}
          >
            {s === "open_due" ? "Open + due" : s}
          </button>
        ))}
      </div>

      <ul className="max-h-[32rem] divide-y overflow-y-auto rounded-xl border border-gray-200">
        {rows.map((row) => (
          <OpsManufacturerPayableRow
            key={row.id}
            row={row}
            busy={busy}
            onMarkDue={markDue}
            onMarkPaid={markPaid}
            onPay={recordPay}
          />
        ))}
        {rows.length === 0 ? (
          <li className="px-3 py-6 text-sm text-gray-500">No manufacturer payables yet.</li>
        ) : null}
      </ul>
    </div>
  );
}
